import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import type { MitChargeLineRequest } from '../../core/models';
import { ProductDTO, ProductService } from '../../services/product.service';

export type MitChargeLinesForm = FormArray<FormGroup>;

const MAX_LINES = 20;

export function buildMitChargeLine(fb: FormBuilder): FormGroup {
  return fb.group({
    product: fb.control<ProductDTO | string | null>(null, (control) =>
      control.value && typeof control.value !== 'string' ? null : { required: true }),
    quantity: fb.control<number | null>(1, [Validators.required, Validators.min(0.01)]),
    unitPrice: fb.control<number | null>(null, [Validators.required, Validators.min(0.01)]),
    description: ['', Validators.maxLength(250)]
  });
}

/** Sin líneas; se agregan desde el popup y se exige al menos una mientras el arreglo esté habilitado. */
export function buildMitChargeLinesForm(fb: FormBuilder): MitChargeLinesForm {
  return fb.array<FormGroup>([], Validators.minLength(1));
}

export function mitChargeLinesFromForm(lines: MitChargeLinesForm): MitChargeLineRequest[] {
  return lines.getRawValue().map((line) => ({
    productId: (line['product'] as ProductDTO).id,
    quantity: line['quantity'] as number,
    unitPrice: (line['unitPrice'] as number | null) ?? undefined,
    description: (line['description'] as string)?.trim() || undefined
  }));
}

export interface MitChargeLineAmounts {
  subtotal: number;
  tax: number;
  total: number;
}

export interface MitChargeLineTaxAmount {
  label: string;
  amount: number;
}

const round = (value: number, digits: number): number => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

/** Subtotal, impuestos y total de una línea como los calcula la API; `null` si falta precio o no se conocen los impuestos. */
export function mitChargeLineAmounts(line: Record<string, unknown>): MitChargeLineAmounts | null {
  const product = line['product'] as ProductDTO | string | null;
  const quantity = line['quantity'] as number | null;
  const unitPrice = line['unitPrice'] as number | null;
  if (!product || typeof product === 'string' || quantity == null || unitPrice == null) return null;
  const rate = taxRate(product);
  if (rate == null) return null;
  const subtotal = round(quantity * unitPrice, 2);
  const tax = round(subtotal * rate, 4);
  return { subtotal, tax, total: round(subtotal + tax, 4) };
}

/** Monto a cobrar: suma de las líneas con impuestos, a centavos; `null` si no hay líneas o alguna no se puede calcular. */
export function mitChargeLinesTotal(lines: MitChargeLinesForm): number | null {
  const values = lines.getRawValue();
  if (values.length === 0) return null;
  let total = 0;
  for (const line of values) {
    const amounts = mitChargeLineAmounts(line);
    if (amounts == null) return null;
    total += amounts.total;
  }
  return round(total, 2);
}

/** Impuestos que la API suma al precio: activos y no exentos. */
function chargedTaxes(product: ProductDTO) {
  return (product.taxes ?? []).filter((tax) => tax.state !== 'INACTIVE' && tax.applicationType !== 'EXEMPT');
}

function decimalRate(rate: number | undefined): number {
  const value = rate ?? 0;
  if (value <= 0) return 0;
  return value > 1 ? value / 100 : value;
}

/** Suma de las tasas de impuestos del producto como fracción; `null` si no se conocen. */
function taxRate(product: ProductDTO): number | null {
  if (!product.taxes) return null;
  return chargedTaxes(product).reduce((sum, tax) => sum + decimalRate(tax.taxRate), 0);
}

/** Precio del producto sin impuestos, como lo calcula la API; `null` si no se puede desglosar. */
function netUnitPrice(product: ProductDTO): number | null {
  if (product.unitPrice == null) return null;
  if (!product.taxInclusive) return product.unitPrice;
  const rate = taxRate(product);
  if (rate == null) return null;
  return round(product.unitPrice / (1 + rate), 2);
}

/**
 * Productos de un cobro MIT o del primer cobro de un mandato: tabla con las líneas y popup para agregarlas o
 * editarlas. Los impuestos salen del producto; el precio que se captura es sin impuestos.
 */
@Component({
  selector: 'app-mit-charge-lines-fields',
  templateUrl: './mit-charge-lines-fields.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, AutoCompleteModule, ButtonModule, DialogModule, FloatLabelModule, InputNumberModule,
    InputTextModule]
})
export class MitChargeLinesFieldsComponent {
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private productService = inject(ProductService);
  private productQuery = new Subject<string>();

  lines = input.required<MitChargeLinesForm>();
  idPrefix = input('mit-lines');

  productOptions = signal<ProductDTO[]>([]);
  readonly maxLines = MAX_LINES;

  dialogVisible = signal(false);
  /** Índice de la línea en edición; `null` al agregar. */
  editingIndex = signal<number | null>(null);
  lineForm = buildMitChargeLine(this.fb);

  constructor() {
    this.productQuery.pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed(inject(DestroyRef))).subscribe((query) => {
      this.productService.getProducts({ 'name[lk]': query }, { page: 0, size: 20 }).subscribe((response) => {
        this.productOptions.set(response.body ?? []);
      });
    });
    // El formulario padre agrega, limpia o marca como tocadas las líneas sin pasar por este componente.
    effect((onCleanup) => {
      const subscription = this.lines().events.subscribe(() => this.cdr.markForCheck());
      onCleanup(() => subscription.unsubscribe());
    });
  }

  onProductSearch(event: { query: string }): void {
    const query = (event.query ?? '').trim();
    if (query.length < 3) {
      this.productOptions.set([]);
      return;
    }
    this.productQuery.next(query);
  }

  /** Precarga el precio sin impuestos del producto; el usuario puede cambiarlo. */
  onProductSelect(product: ProductDTO): void {
    this.lineForm.controls['unitPrice'].setValue(netUnitPrice(product));
  }

  openAdd(): void {
    if (this.lines().length >= MAX_LINES || this.lines().disabled) return;
    this.editingIndex.set(null);
    this.lineForm.reset({ product: null, quantity: 1, unitPrice: null, description: '' });
    this.dialogVisible.set(true);
  }

  openEdit(index: number): void {
    if (this.lines().disabled) return;
    this.editingIndex.set(index);
    this.lineForm.reset(this.lines().at(index).getRawValue());
    this.dialogVisible.set(true);
  }

  saveLine(): void {
    this.lineForm.markAllAsTouched();
    if (this.lineForm.invalid) return;
    const value = this.lineForm.getRawValue();
    const index = this.editingIndex();
    if (index == null) {
      const line = buildMitChargeLine(this.fb);
      line.setValue(value);
      this.lines().push(line);
    } else {
      this.lines().at(index).setValue(value);
    }
    this.lines().markAsDirty();
    this.dialogVisible.set(false);
  }

  remove(index: number): void {
    if (this.lines().disabled) return;
    this.lines().removeAt(index);
    this.lines().markAsTouched();
  }

  closeDialog(): void {
    this.dialogVisible.set(false);
  }

  description(line: FormGroup): string {
    const value = line.getRawValue();
    const product = value['product'] as ProductDTO | null;
    return (value['description'] as string)?.trim() || product?.name || '';
  }

  lineAmounts(line: FormGroup): MitChargeLineAmounts | null {
    return mitChargeLineAmounts(line.getRawValue());
  }

  totals(): MitChargeLineAmounts | null {
    const values = this.lines().getRawValue();
    if (values.length === 0) return null;
    let subtotal = 0;
    let tax = 0;
    for (const value of values) {
      const amounts = mitChargeLineAmounts(value);
      if (amounts == null) return null;
      subtotal += amounts.subtotal;
      tax += amounts.tax;
    }
    return { subtotal: round(subtotal, 2), tax: round(tax, 4), total: mitChargeLinesTotal(this.lines()) ?? 0 };
  }

  /** Producto elegido en el popup, si ya se seleccionó uno del catálogo. */
  dialogProduct(): ProductDTO | null {
    const product = this.lineForm.controls['product'].value as ProductDTO | string | null;
    return product && typeof product !== 'string' ? product : null;
  }

  /** Cantidad × precio unitario a centavos, como lo calcula la API. */
  dialogSubtotal(): number | null {
    const quantity = this.lineForm.controls['quantity'].value as number | null;
    const unitPrice = this.lineForm.controls['unitPrice'].value as number | null;
    return quantity != null && unitPrice != null ? round(quantity * unitPrice, 2) : null;
  }

  dialogAmounts(): MitChargeLineAmounts | null {
    return mitChargeLineAmounts(this.lineForm.getRawValue());
  }

  /** Desglose por impuesto de la línea en el popup. */
  dialogTaxes(): MitChargeLineTaxAmount[] {
    const product = this.dialogProduct();
    const amounts = this.dialogAmounts();
    if (!product || !amounts) return [];
    return chargedTaxes(product).map((tax) => ({
      label: tax.tax?.displayName || `Impuesto ${round(decimalRate(tax.taxRate) * 100, 4)}%`,
      amount: round(amounts.subtotal * decimalRate(tax.taxRate), 4)
    }));
  }

  /** El precio de catálogo incluye impuestos: se muestra junto al precio sin impuestos precargado. */
  catalogPriceIncludesTaxes(): boolean {
    const product = this.dialogProduct();
    return !!product?.taxInclusive && chargedTaxes(product).length > 0;
  }

  showError(): boolean {
    const lines = this.lines();
    return lines.enabled && lines.length === 0 && lines.touched;
  }

  invalid(name: string): boolean {
    const control = this.lineForm.get(name);
    return !!control && control.invalid && (control.touched || control.dirty);
  }
}
